import React, { useState, useRef, useEffect } from 'react';
import { Save, Upload, User, Image as ImageIcon, Palette, Type, Globe, Phone, Mail, Facebook, Twitter, Instagram, Linkedin, Shield } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import OrganizationRolesTab from '../components/users/OrganizationRolesTab';
import { useToastContext } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { settingsAPI } from '../api/endpoints';

const OrganizationSettings = () => {
  const toast = useToastContext();
  const { user, login } = useAuth(); // use login to update user state if needed
  
  const [activeTab, setActiveTab] = useState('branding');
  const [saving, setSaving] = useState(false);
  
  // Branding
  const [organizationName, setOrganizationName] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  
  // Settings
  const [settings, setSettings] = useState({
    theme_primary_color: '#4F46E5',
    theme_secondary_color: '#10B981',
    theme_background_color: '#F9FAFB',
    theme_text_color: '#111827',
    theme_font_family: 'Inter',
    contact_email: '',
    contact_phone: '',
    website_url: '',
    social_facebook: '',
    social_twitter: '',
    social_linkedin: '',
    social_instagram: ''
  });
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user) {
      setOrganizationName(user.organization_name || '');
      setLogoPreview(user.organization_logo || null);
      if (user.organization_settings) {
        setSettings(prev => ({ ...prev, ...user.organization_settings }));
      }
    }
  }, [user]);

  const handleSettingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      if (organizationName) {
        formData.append('organization_name', organizationName);
      }
      if (logoFile) {
        formData.append('organization_logo', logoFile);
      }
      
      // Append settings array
      Object.entries(settings).forEach(([key, value]) => {
        if (value) {
          formData.append(`organization_settings[${key}]`, value);
        }
      });

      const response = await settingsAPI.updateOrganization(formData);
      
      toast.success('Organization settings updated successfully!');
      
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update organization settings');
    } finally {
      setSaving(false);
    }
  };

  if (!user || (!user.isEventAdmin && user.role !== 'super_admin' && user.role !== 'event_admin')) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You must be an event administrator to manage organization settings.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const tabs = [
    { id: 'branding', label: 'Branding & Logo', icon: ImageIcon },
    { id: 'theme', label: 'Global Theme', icon: Palette },
    { id: 'roles', label: 'Roles & Permissions', icon: Shield },
    { id: 'contact', label: 'Contact Info', icon: Mail },
    { id: 'social', label: 'Social Links', icon: Globe },
  ];

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Organization Settings</h1>
            <p className="text-gray-500">Manage your global organization branding and defaults.</p>
          </div>
          <div className="flex items-center gap-3">
            {activeTab !== 'roles' && (
              <Button
                variant="primary"
                onClick={handleSave}
                loading={saving}
              >
                <Save className="w-4 h-4 mr-2 inline" />
                Save Settings
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon
                    className={`
                      -ml-0.5 mr-2 h-5 w-5
                      ${activeTab === tab.id ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500'}
                    `}
                  />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'roles' && <OrganizationRolesTab />}
          
          {activeTab === 'branding' && (
            <Card>
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
                  <p className="text-sm text-gray-500 mb-2">This name will be used in emails sent to attendees and on your event registration pages.</p>
                  <input
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g. Acme Corp Events"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization Logo</label>
                  <p className="text-sm text-gray-500 mb-4">This logo will appear on your registration pages. Recommended size: 400x150px. Max size: 5MB.</p>
                  
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      {logoPreview ? (
                        <div className="relative w-48 h-32 border border-gray-200 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                          <img src={logoPreview} alt="Logo preview" className="max-w-full max-h-full object-contain" />
                          <button
                            onClick={() => {
                              setLogoPreview(null);
                              setLogoFile(null);
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }}
                            className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                          >
                            <span className="sr-only">Remove</span>
                            <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="w-48 h-32 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 flex flex-col items-center justify-center text-gray-400">
                          <ImageIcon className="w-8 h-8 mb-2" />
                          <span className="text-sm">No logo</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleLogoChange}
                        accept="image/*"
                        className="hidden"
                      />
                      <Button
                        variant="secondary"
                        onClick={() => fileInputRef.current?.click()}
                        className="mb-2"
                      >
                        <Upload className="w-4 h-4 mr-2 inline" />
                        Upload Logo
                      </Button>
                      <p className="text-xs text-gray-500">Accepts JPG, PNG, GIF, WEBP</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'theme' && (
            <Card>
              <div className="p-6 space-y-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Global Theme</h3>
                  <p className="text-sm text-gray-500">These colors will be used as the default for all new events you create.</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        name="theme_primary_color"
                        value={settings.theme_primary_color}
                        onChange={handleSettingChange}
                        className="h-10 w-20 p-1 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        name="theme_primary_color"
                        value={settings.theme_primary_color}
                        onChange={handleSettingChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        name="theme_secondary_color"
                        value={settings.theme_secondary_color}
                        onChange={handleSettingChange}
                        className="h-10 w-20 p-1 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        name="theme_secondary_color"
                        value={settings.theme_secondary_color}
                        onChange={handleSettingChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        name="theme_background_color"
                        value={settings.theme_background_color}
                        onChange={handleSettingChange}
                        className="h-10 w-20 p-1 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        name="theme_background_color"
                        value={settings.theme_background_color}
                        onChange={handleSettingChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        name="theme_text_color"
                        value={settings.theme_text_color}
                        onChange={handleSettingChange}
                        className="h-10 w-20 p-1 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        name="theme_text_color"
                        value={settings.theme_text_color}
                        onChange={handleSettingChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
                    <div className="flex items-center gap-3">
                      <Type className="w-5 h-5 text-gray-400" />
                      <select
                        name="theme_font_family"
                        value={settings.theme_font_family}
                        onChange={handleSettingChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="Inter">Inter (Default)</option>
                        <option value="Roboto">Roboto</option>
                        <option value="Open Sans">Open Sans</option>
                        <option value="Playfair Display">Playfair Display</option>
                        <option value="Montserrat">Montserrat</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'contact' && (
            <Card>
              <div className="p-6 space-y-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Contact Information</h3>
                  <p className="text-sm text-gray-500">These details may appear in email footers and support sections.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Support Email</label>
                    <div className="flex items-center gap-2">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        name="contact_email"
                        value={settings.contact_email}
                        onChange={handleSettingChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="support@acmecorp.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Support Phone</label>
                    <div className="flex items-center gap-2">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        name="contact_phone"
                        value={settings.contact_phone}
                        onChange={handleSettingChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                    <div className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-gray-400" />
                      <input
                        type="url"
                        name="website_url"
                        value={settings.website_url}
                        onChange={handleSettingChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="https://www.acmecorp.com"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'social' && (
            <Card>
              <div className="p-6 space-y-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Social Links</h3>
                  <p className="text-sm text-gray-500">Connect your organization's social profiles to automatically generate footer links.</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
                    <div className="flex items-center gap-2">
                      <Facebook className="w-5 h-5 text-blue-600" />
                      <input
                        type="url"
                        name="social_facebook"
                        value={settings.social_facebook}
                        onChange={handleSettingChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="https://facebook.com/yourpage"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Twitter / X</label>
                    <div className="flex items-center gap-2">
                      <Twitter className="w-5 h-5 text-sky-500" />
                      <input
                        type="url"
                        name="social_twitter"
                        value={settings.social_twitter}
                        onChange={handleSettingChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="https://twitter.com/yourhandle"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                    <div className="flex items-center gap-2">
                      <Instagram className="w-5 h-5 text-pink-600" />
                      <input
                        type="url"
                        name="social_instagram"
                        value={settings.social_instagram}
                        onChange={handleSettingChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="https://instagram.com/yourhandle"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
                    <div className="flex items-center gap-2">
                      <Linkedin className="w-5 h-5 text-blue-700" />
                      <input
                        type="url"
                        name="social_linkedin"
                        value={settings.social_linkedin}
                        onChange={handleSettingChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="https://linkedin.com/company/yourcompany"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default OrganizationSettings;
